/**
 * Shell for the authenticated application.
 *
 * Renders only beneath the route guard, so a session is guaranteed to exist by the time this
 * mounts. It still reads the session from the query cache rather than taking it as a prop, so a
 * logout elsewhere in the tree updates the header without a reload.
 */
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { cn } from '@urlshot/ui';
import { useLogout, useSession } from '../lib/api';
import { DocsLink } from '../components/docs-link';
import { Logo } from '../components/logo';

const NAVIGATION = [
  { to: '/overview', label: 'Overview' },
  { to: '/playground', label: 'Playground' },
  { to: '/api-keys', label: 'API keys' },
  { to: '/request-logs', label: 'Request logs' },
  { to: '/workspace', label: 'Workspace' },
  { to: '/billing', label: 'Billing' },
  { to: '/settings', label: 'Settings' },
] as const;

export function AppLayout() {
  const { data: session } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout.mutate(undefined, {
      // `onSettled` in the hook clears the cache and the hint cookie; navigating here rather than
      // there keeps routing concerns in the component that owns them.
      onSettled: () => void navigate({ to: '/login' }),
    });
  };

  return (
    <div className="min-h-dvh">
      {/* Dark, as the marketing site's header is, so moving between the two reads as one product. */}
      <header className="bg-neutral-900">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            {/* Home, as the same mark is on the site: here, home is the overview. */}
            <Link to="/overview" className="shrink-0">
              <Logo />
            </Link>
            {session?.workspace !== undefined && (
              <span className="rounded-full bg-neutral-800 px-2.5 py-0.5 text-xs text-neutral-300">
                {session.workspace.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {session?.user !== undefined && (
              <span className="text-sm text-neutral-400">{session.user.email}</span>
            )}
            {/* Not the light `Button`: on the dark bar it takes the site's outlined pill instead. */}
            <button
              type="button"
              onClick={handleLogout}
              disabled={logout.isPending}
              className="rounded-full border border-neutral-600 px-4 py-1.5 text-sm text-brand-400 transition-colors hover:border-neutral-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {logout.isPending ? 'Signing out...' : 'Sign out'}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-6xl gap-8 px-6 py-8">
        <nav aria-label="Dashboard" className="w-44 shrink-0">
          <ul className="flex flex-col gap-1">
            {NAVIGATION.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={cn(
                    'block rounded-md px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100',
                  )}
                  activeProps={{
                    className: 'bg-brand-100 font-medium text-neutral-900 hover:bg-brand-100',
                  }}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          {/*
            Apart from the list because it is not a place in the dashboard: it opens the site's
            documentation in a new tab, and sitting among the routes it would read as one.
          */}
          <div className="mt-4 border-t border-neutral-200 pt-4">
            <DocsLink className="flex items-center justify-between rounded-md px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100">
              Documentation
              <span aria-hidden="true">↗</span>
            </DocsLink>
          </div>
        </nav>

        <main className="flex min-w-0 flex-1 flex-col gap-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
